/**
 * "Code here, not there" recipient guard (#144 / R2).
 *
 * ── WHAT LOSES THE MONEY ─────────────────────────────────────────────────────────────────────────
 * The same 0x address is assumed to exist on all six EVM chains. That is true of an EOA and FALSE of
 * a contract: a Safe on Base is not necessarily deployed at the same address on Arbitrum (that needs
 * identical factory, salt and nonce). CCTP mints straight to the recipient — our contract is not in
 * the destination path at all (`destinationCaller = bytes32(0)`) — and the testnet probe settled the
 * load-bearing question: minting USDC to a CODELESS address SUCCEEDS SILENTLY at both the FiatToken
 * and TokenMinter level. The only recipient validation anywhere in the whole path is a zero-address
 * check. So the funds land at an address nobody controls, with no revert and no error, anywhere.
 *
 * A contract redeploy cannot fix this. Destination-side, "codeless" is indistinguishable between a
 * normal EOA (fine) and an undeployed Safe (total loss) — any on-chain check must allow both or
 * reject both, and rejecting both breaks every ordinary user. The only distinguishing fact lives on
 * ANOTHER chain, and Solidity cannot read it. The client is not the cheap interim option here; it is
 * the only place the predicate can be evaluated at all.
 *
 * ── THE PREDICATE, AND WHY IT IS NOT hasCode(recipient, sourceChain) ──────────────────────────────
 * The obvious form — code on the SOURCE chain, none on the destination — no-ops on this issue's own
 * headline path: source Solana, destination EVM, Safe connected over WalletConnect, recipient
 * silently defaults to the Safe with ZERO user action. `hasCode(recipient, 'solana')` is meaningless.
 * That same path also passes #143 untouched, because the Safe genuinely IS the connected wallet.
 *
 * So the predicate sweeps ALL SIX configured EVM chains:
 *
 *     code on ANY EVM chain (excluding 7702 delegations) && no code on the destination
 *
 * which additionally catches the third-chain case (Safe deployed only on Polygon, bridging
 * Ethereum → Arbitrum) that a source-vs-dest comparison misses.
 *
 * ── WARN + CONFIRM. NEVER HARD-BLOCK. ────────────────────────────────────────────────────────────
 * Two legitimate cohorts satisfy the predicate and would be bricked by a block:
 *
 *  1. EIP-7702 delegated EOAs carry 23 bytes of `0xef0100‖address` code on the delegating chain and
 *     none elsewhere. They are EOAs. The prefix is carved out below as EOA-equivalent — a byte test
 *     on data already fetched, costing nothing.
 *
 *     NOTE FOR ANYONE WHO READS `apps/api/src/utils/onchain-sender.ts` FIRST: its header states that
 *     EIP-7702 needs no special case. That is TRUE THERE and FALSE HERE, and they are different
 *     questions. There, the question is "who authorised this transaction?" — a 7702 EOA sending its
 *     own tx still produces `receipt.from == user`, so nothing special is needed. Here, the question
 *     is "does this address's code mean it is a contract that might not exist on the destination?" —
 *     and for 7702 the answer is no. Do not delete the carve-out because of that comment.
 *
 *  2. Counterfactual ERC-4337 accounts (deployed on source, undeployed on destination) are SAFE —
 *     the factory is deterministic and same-address — and are the #137 target cohort. Blocking them
 *     defeats the feature this round exists to finish.
 *
 * ── THE CEILING OF THIS GUARD ────────────────────────────────────────────────────────────────────
 * It proves "code exists", never "the user controls it". Same-address-different-controller across
 * chains is real (cf. Wintermute/Optimism). The copy must not claim verification, and the caller's
 * confirmation prompt must not either.
 *
 * ── PERFORMANCE IS A CORRECTNESS PROPERTY HERE, NOT POLISH ───────────────────────────────────────
 * A plain EOA is the STRUCTURAL WORST CASE: the only short-circuit is "code exists on the
 * destination", and an EOA has code nowhere, so all six reads run. They hit six separate public
 * endpoints (`config/wagmi.ts:61-64` documents IN-REPO that these already rate-limit in-browser) and
 * are not batchable. Four mitigations, all mandatory:
 *   - the DESTINATION is read FIRST and short-circuits the other five when it has code
 *   - every (address, chainId) result is memoised for the session, so a dest-chain switch re-reads
 *     one chain rather than six, and the revalidation at signing time is free
 *   - the sweep has a hard wall-clock budget and degrades to a WARN, never to a block
 *   - callers prefetch on (recipient, destChain) change so the reads overlap the user's own dwell
 *     time on the review screen instead of adding latency in front of the wallet prompt
 *
 * ── WHY THE MODULE-SINGLETON wagmiConfig, NOT useConfig() ────────────────────────────────────────
 * `useDexBridge` deliberately binds `useConfig()` for its WRITES (see the provenance note in
 * `utils/erc20Approve.ts` — swapping that object would change which config actually broadcasts).
 * This guard only ever issues read-only `eth_getCode` and must go through the app's OWN transports,
 * exactly as `utils/smartAccount.ts` does: asking the connected wallet's provider whether an address
 * has code is asking a potentially-interested party a question about itself.
 *
 * ── FAILURE POLICY ───────────────────────────────────────────────────────────────────────────────
 * One retry per read, then "could not verify" — which is a WARN, not a block and not silence.
 * Hard-blocking on an RPC blip would brick bridging during a publicnode wobble; failing open
 * silently would delete the guard exactly when an attacker (or a bad day) wants it deleted.
 */
import { getBytecode } from '@wagmi/core'
import { wagmiConfig } from '@/config/wagmi'
import { TESTNET_EVM_CHAIN_IDS, MAINNET_EVM_CHAIN_IDS } from '@/config/chainSets'
import type { Chain } from '@/types'

const isTestnet = (import.meta.env.VITE_NETWORK || 'testnet') === 'testnet'

/**
 * The six EVM chains swept, per network — DERIVED from `config/chainSets.ts`, which is also what
 * `config/wagmi.ts` builds its `chains` and `transports` from. The two therefore cannot drift: there
 * is one list, not two copies that a test has to keep honest. This matters because a silently-dropped
 * chain would be a hole in the sweep with no other symptom.
 *
 * The previous version restated the twelve ids as literals here and relied on
 * `recipientCodeGuard.test.ts` to catch divergence — but that test mocked `@/config/wagmi`, so it
 * compared this constant against a constant in the test file and could not detect drift at all.
 * `chainSets.test.ts` now pins every derivation instead, on BOTH networks, independent of
 * `VITE_NETWORK`.
 */
export const GUARDED_EVM_CHAIN_IDS: Readonly<Record<string, number>> = isTestnet
  ? TESTNET_EVM_CHAIN_IDS
  : MAINNET_EVM_CHAIN_IDS

/** EIP-7702 delegation designator. Code is exactly `0xef0100 ‖ 20-byte address` = 23 bytes. */
export const EIP7702_DELEGATION_PREFIX = '0xef0100'

/** Wall-clock ceiling for the five non-destination reads, after which we degrade to "unverified". */
export const RECIPIENT_CODE_SWEEP_BUDGET_MS = 2500

/** How long a DEFINITE read is trusted. Bounded because a counterfactual account can be deployed
 *  mid-session, which would otherwise pin a stale "no code anywhere" for the whole tab's life. */
const CODE_CACHE_TTL_MS = 5 * 60 * 1000

/** Result of one `eth_getCode`. `unknown` is never cached — an RPC blip must not become sticky. */
export type CodeKind = 'none' | 'contract' | 'delegated' | 'unknown'

export type RecipientCodeVerdict =
  | {
      kind: 'ok'
      reason:
        | 'non-evm-destination'
        | 'not-an-evm-address'
        | 'deployed-on-destination'
        | 'no-code-anywhere'
    }
  | { kind: 'warn'; reason: 'code-here-not-there'; chainsWithCode: string[] }
  | { kind: 'warn'; reason: 'unverified'; chainsWithCode: string[] }

export interface RecipientCodeRisk {
  recipient: string
  destChain: Chain | null
  verdict: Extract<RecipientCodeVerdict, { kind: 'warn' }>
}

/** Asks the user to accept the risk. Resolving false must abort the transfer BEFORE any signing. */
export type RecipientRiskConfirm = (risk: RecipientCodeRisk) => Promise<boolean>

/** Thrown when the user declines. Callers surface it as a cancellation, never as a failed transfer. */
export class RecipientRiskDeclinedError extends Error {
  constructor() {
    super('Transfer cancelled — recipient not confirmed')
    this.name = 'RecipientRiskDeclinedError'
  }
}

const EVM_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/

interface CacheEntry { kind: Exclude<CodeKind, 'unknown'>; at: number }
const codeCache = new Map<string, CacheEntry>()
/** De-duplicates concurrent reads of the same key (prefetch racing the revalidation at signing). */
const inFlight = new Map<string, Promise<CodeKind>>()

/** Exported for tests, which must not inherit another test's memoised chain state. */
export function clearRecipientCodeCache(): void {
  codeCache.clear()
  inFlight.clear()
}

function classify(bytecode: string | undefined | null): Exclude<CodeKind, 'unknown'> {
  // TRAP (same one `smartAccount.ts` documents): viem maps '0x' → undefined, so the natural-looking
  // `bytecode !== '0x'` is TRUE for every plain EOA. All three empty shapes mean "no code".
  if (typeof bytecode !== 'string' || bytecode === '' || bytecode === '0x') return 'none'
  // EIP-7702: an EOA that delegated. NOT a contract for this guard's purposes — see header.
  if (bytecode.toLowerCase().startsWith(EIP7702_DELEGATION_PREFIX)) return 'delegated'
  return 'contract'
}

/** One `eth_getCode` with a single retry, memoised. Never throws. */
async function readCodeKind(address: string, chainId: number): Promise<CodeKind> {
  const key = `${chainId}:${address.toLowerCase()}`

  const cached = codeCache.get(key)
  if (cached && Date.now() - cached.at < CODE_CACHE_TTL_MS) return cached.kind
  if (cached) codeCache.delete(key)

  const existing = inFlight.get(key)
  if (existing) return existing

  const task = (async (): Promise<CodeKind> => {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const bytecode = await getBytecode(wagmiConfig, {
          address: address as `0x${string}`,
          // Cast only to satisfy wagmi's literal chain-id union; every id here is a configured chain.
          chainId: chainId as (typeof wagmiConfig)['chains'][number]['id'],
        })
        const kind = classify(bytecode)
        codeCache.set(key, { kind, at: Date.now() })
        return kind
      } catch {
        // One retry, then give up. Deliberately not cached: a transient failure must not become a
        // permanent "unverified" for the rest of the session.
      }
    }
    return 'unknown'
  })().finally(() => inFlight.delete(key))

  inFlight.set(key, task)
  return task
}

/**
 * Evaluate the "code here, not there" predicate for `recipient` on `destChain`.
 *
 * Never throws and never blocks: every failure mode resolves to a verdict, and the worst verdict is
 * a warning. Solana and Tron destinations return `ok` immediately without issuing any RPC — getCode
 * is meaningless there, and a crash on a non-EVM destination would take out a working path.
 */
export async function evaluateRecipientCode(args: {
  recipient: string | null | undefined
  destChain: Chain | null
}): Promise<RecipientCodeVerdict> {
  const { recipient, destChain } = args

  const destChainId = destChain ? GUARDED_EVM_CHAIN_IDS[destChain] : undefined
  if (destChainId === undefined) return { kind: 'ok', reason: 'non-evm-destination' }
  if (!recipient || !EVM_ADDRESS_RE.test(recipient)) {
    // Shape validation belongs to RecipientModal / validateAddressForChain. Nothing to check here.
    return { kind: 'ok', reason: 'not-an-evm-address' }
  }

  // ── Destination FIRST. This is the only short-circuit that exists, so it must come first. ───────
  const destKind = await readCodeKind(recipient, destChainId)
  if (destKind === 'contract' || destKind === 'delegated') {
    // Code exists at the destination — a deployed contract there is a legitimate, working recipient
    // (a plain ERC-20 balance increment, no callback), and a 7702 delegation means the EOA is live.
    return { kind: 'ok', reason: 'deployed-on-destination' }
  }
  if (destKind === 'unknown') {
    // We could not establish the load-bearing fact. Warn; never block, never pretend.
    return { kind: 'warn', reason: 'unverified', chainsWithCode: [] }
  }

  // ── Destination is codeless. Sweep the other five, under a hard budget. ─────────────────────────
  const others = Object.entries(GUARDED_EVM_CHAIN_IDS).filter(([, id]) => id !== destChainId)
  const results = new Map<string, CodeKind>()

  let timer: ReturnType<typeof setTimeout> | undefined
  const budget = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, RECIPIENT_CODE_SWEEP_BUDGET_MS)
  })
  const sweep = Promise.all(
    others.map(async ([name, id]) => { results.set(name, await readCodeKind(recipient, id)) }),
  ).then(() => undefined)

  try {
    // Whichever finishes first. On budget expiry we grade on the partial results already collected —
    // a slow endpoint degrades the verdict to "unverified", it never stalls the wallet prompt.
    await Promise.race([sweep, budget])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }

  const chainsWithCode = others
    .map(([name]) => name)
    .filter((name) => results.get(name) === 'contract')

  if (chainsWithCode.length > 0) {
    return { kind: 'warn', reason: 'code-here-not-there', chainsWithCode }
  }

  // No contract code found — but only trust that if every chain actually answered. A timed-out or
  // failed read is NOT evidence of absence, and silently treating it as such is how a guard becomes
  // decorative. Warn as "unverified" instead.
  const allAnswered = others.every(([name]) => {
    const kind = results.get(name)
    return kind === 'none' || kind === 'delegated'
  })
  if (!allAnswered) return { kind: 'warn', reason: 'unverified', chainsWithCode: [] }

  return { kind: 'ok', reason: 'no-code-anywhere' }
}

/**
 * Warm the memo for a (recipient, destChain) pair. Fire-and-forget: callers invoke this when the
 * pair CHANGES so the six reads overlap the user's dwell time on the review screen, and the
 * revalidation at signing time is a cache hit costing zero round-trips.
 */
export function prefetchRecipientCode(args: {
  recipient: string | null | undefined
  destChain: Chain | null
}): void {
  void evaluateRecipientCode(args).catch(() => undefined)
}

/**
 * Run the guard against the EXACT (recipient, destChain) pair about to be signed, and abort if the
 * user declines.
 *
 * Placement matters and is the reason this is a function rather than a hook: it must run inside the
 * signing path, not at review-modal open. `prevDestChainRef` mutates before the early return in
 * BridgePage's destination-sync effect, so a verdict computed when the modal opened can be stale by
 * the time the burn is actually signed.
 *
 * `confirm` is deliberately REQUIRED and has no default. A guard that silently proceeds when no
 * prompt was wired is a guard that gets dropped by accident in a refactor; making it a type error
 * forces every new signing path to answer the question.
 */
export async function assertRecipientCodeAccepted(args: {
  recipient: string | null | undefined
  destChain: Chain | null
  confirm: RecipientRiskConfirm
}): Promise<void> {
  const verdict = await evaluateRecipientCode({ recipient: args.recipient, destChain: args.destChain })
  if (verdict.kind === 'ok') return

  const accepted = await args.confirm({
    recipient: args.recipient ?? '',
    destChain: args.destChain,
    verdict,
  })
  if (!accepted) throw new RecipientRiskDeclinedError()
}
